import React from 'react';
import {flow, identity} from 'lodash';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {Atom, Plugins, type AtomRenderProps} from '@packages/systems/wfdl';
import type {List as IList} from 'immutable';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useSite} from '@packages/systems/render';

function pushIf(condition: boolean, className: string) {
  return condition
    ? (classes: IList<string>) => classes.push(className)
    : identity;
}

const DATA_ATTR_SITEKEY = 'data-sitekey';

const FormReCaptcha = Atom.createRender((props: AtomRenderProps) => {
  const {attributes, classes, generateAttributes, context} = props;
  const runtimeEnvironment = useRuntimeEnvironment();
  const site = useSite();

  const required =
    runtimeEnvironment === WebflowEnvironments.DESIGNER
      ? context.get('recaptchaRequired')
      : // @ts-expect-error recaptchaRequired is not on SiteType
        site.get('recaptchaRequired');

  const siteKey =
    runtimeEnvironment === WebflowEnvironments.DESIGNER
      ? context.get('recaptchaSiteKey')
      : site.get('recaptchaSiteKey');

  const attributesWithSiteKey = siteKey
    ? attributes.set(DATA_ATTR_SITEKEY, siteKey)
    : attributes;

  const recaptchaClasses = flow(
    pushIf(true, 'g-recaptcha'), // Always include Google's required class
    pushIf(!required || !siteKey, 'g-recaptcha-error'), // Include if there's any sort of error
    pushIf(!required, 'g-recaptcha-disabled'), // Include if reCAPTCHA not currently enabled site-wide
    pushIf(!siteKey, 'g-recaptcha-invalid-key') // Include if the key is missing
  )(classes);

  return (
    <div {...generateAttributes(attributesWithSiteKey, recaptchaClasses)} />
  );
});

const lockedStyleConfig = {
  when: true,
  message: 'The reCAPTCHA has limited styling capabilities.',
} as const;

const config = createDevlinkAtom({
  constraints: {
    ancestors: [
      {
        is: 'FormForm',
        rule: 'ExactlyOne',
      },
    ],
  },
  displayName: 'reCAPTCHA',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('ReCaptcha')),
  },
  showEmpty: false,
  style: {
    base: {
      main: {
        noPseudo: {
          marginBottom: px(8),
        },
      },
    },
  },
  // the following Flow ignore is only for `border` and `borderRadius` because
  // we have typed this based on WebflowCssProperties which is the CSS we
  // _output_, but not the way we structure it in the UI for _inputs_ which is
  // what the constraint should be based on as these are only a UI concern.

  lockedStyles: {
    display: {
      when: true,
      message: 'The reCAPTCHA component’s display setting can’t be edited.',
    },
    paddingTop: lockedStyleConfig,
    paddingBottom: lockedStyleConfig,
    paddingRight: lockedStyleConfig,
    paddingLeft: lockedStyleConfig,
    minWidth: lockedStyleConfig,
    minHeight: lockedStyleConfig,
    maxWidth: lockedStyleConfig,
    maxHeight: lockedStyleConfig,
    width: {
      when: true,
      message:
        'The reCAPTCHA component requires fixed width and height values.',
    },
    height: {
      when: true,
      message:
        'The reCAPTCHA component requires fixed width and height values.',
    },
    backgroundImage: lockedStyleConfig,
    backgroundColor: lockedStyleConfig,
    fontFamily: lockedStyleConfig,
    fontWeight: lockedStyleConfig,
    fontSize: lockedStyleConfig,
    fontStyle: lockedStyleConfig,
    color: lockedStyleConfig,
    lineHeight: lockedStyleConfig,
    letterSpacing: lockedStyleConfig,
    // @ts-expect-error - TS2322 - Type '{ display: { when: true; message: string; }; paddingTop: { readonly when: true; readonly message: "The reCAPTCHA has limited styling capabilities."; }; paddingBottom: { readonly when: true; readonly message: "The reCAPTCHA has limited styling capabilities."; }; ... 30 more ...; borderRadius: { readonly when: true; r...' is not assignable to type 'Partial<Record<keyof WebflowCssProperties, LockedStyleType>>'.
    textColumns: lockedStyleConfig,
    textIndent: lockedStyleConfig,
    textAlign: lockedStyleConfig,
    textDecoration: lockedStyleConfig,
    textShadow: lockedStyleConfig,
    textTransform: lockedStyleConfig,
    direction: lockedStyleConfig,
    columnCount: lockedStyleConfig,
    opacity: lockedStyleConfig,
    transform: lockedStyleConfig,
    transition: lockedStyleConfig,
    filter: lockedStyleConfig,
    border: lockedStyleConfig,
    borderRadius: lockedStyleConfig,
  },
  render: FormReCaptcha,
  fields: {},
});

export default config;
